import { FC, useMemo } from 'react';

import { StyledWheelActiveRow, StyledWheelGroup, WheelColumn } from '../Wheel';
import { StyledContainer, StyledSeparator } from './TimePicker.styled';
import { TimePickerProps } from './types';

const FALLBACK_TIME = '09:00';
const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

const pad = (value: number): string => String(value).padStart(2, '0');

const parse = (time: string | null, fallback: string): { hour: number; minute: number } => {
  const matched = TIME_PATTERN.exec(time ?? '') ?? TIME_PATTERN.exec(fallback) ?? undefined;
  if (!matched) return { hour: 9, minute: 0 };

  return { hour: Number(matched[1]), minute: Number(matched[2]) };
};

/**
 * Inline 24-hour time wheel, rendered in place rather than in a popup.
 *
 * `<input type="time">` hands the choice to the browser's own picker, which ignores the theme and
 * opens a floating layer this design system does not control — inside a popover or drawer it draws
 * over the surface that launched it. Rendering inline keeps one floating layer on screen at a time,
 * the same reason `SetDatePicker` is inline, and both share the `Wheel` primitive.
 */
const TimePicker: FC<TimePickerProps> = ({
  value,
  onChange,
  defaultTime = FALLBACK_TIME,
  minuteStep = 1,
  hourLabel = 'Hour',
  minuteLabel = 'Minute',
}) => {
  const { hour, minute } = parse(value, defaultTime);

  const hours = useMemo(() => Array.from({ length: 24 }, (_, index) => pad(index)), []);
  const minutes = useMemo(
    () => Array.from({ length: Math.ceil(60 / minuteStep) }, (_, index) => pad(index * minuteStep)),
    [minuteStep],
  );

  // The wheel can only settle on a value it renders, so a minute outside the step lands on the
  // nearest row below rather than scrolling to an index that does not exist.
  const minuteIndex = Math.min(Math.floor(minute / minuteStep), minutes.length - 1);

  const emit = (nextHour: number, nextMinute: number) => {
    onChange(`${pad(nextHour)}:${pad(nextMinute)}`);
  };

  return (
    <StyledContainer>
      <StyledWheelGroup>
        <StyledWheelActiveRow />
        <WheelColumn
          items={hours}
          selectedIndex={hour}
          onSelect={(index) => {
            emit(index, Number(minutes[minuteIndex]));
            return index;
          }}
          aria-label={hourLabel}
        />
        <StyledSeparator aria-hidden>:</StyledSeparator>
        <WheelColumn
          items={minutes}
          selectedIndex={minuteIndex}
          onSelect={(index) => {
            emit(hour, Number(minutes[index]));
            return index;
          }}
          aria-label={minuteLabel}
        />
      </StyledWheelGroup>
    </StyledContainer>
  );
};

TimePicker.displayName = 'TimePicker';

export default TimePicker;
